/**
 * RBAC API Client Functions
 * Functions to interact with backend RBAC endpoints
 */

import {
  AssignRolesRequest,
  CreateRoleRequest,
  GetPermissionsResponse,
  GetUserPermissionsResponse,
  ListRolesResponse,
  ListUsersResponse,
  Role,
  UpdateRoleRequest,
  UserProfile,
  UserRoleAuditLog,
} from "../types/rbac";
import { APIError } from "../utils/ErrorHandling";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const defaultHeaders: HeadersInit = {
  "Content-Type": "application/json",
};

/**
 * Generic GET request
 */
async function get<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: "GET",
    headers: defaultHeaders,
    credentials: "include",
  });

  if (response.ok) {
    return response.json() as Promise<T>;
  }

  const errorData = await response.json();
  throw new APIError(
    errorData?.message || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

/**
 * Generic POST request
 */
async function post<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: defaultHeaders,
    body: JSON.stringify(body),
    credentials: "include",
  });

  if (response.ok) {
    return response.json() as Promise<T>;
  }

  const errorData = await response.json();
  throw new APIError(
    errorData?.message || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

/**
 * Generic PUT request
 */
async function put<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "PUT",
    headers: defaultHeaders,
    body: JSON.stringify(body),
    credentials: "include",
  });

  if (response.ok) {
    return response.json() as Promise<T>;
  }

  const errorData = await response.json();
  throw new APIError(
    errorData?.message || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

/**
 * Generic DELETE request
 */
async function deleteRequest<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: "DELETE",
    headers: defaultHeaders,
    credentials: "include",
  });

  if (response.ok) {
    return response.json() as Promise<T>;
  }

  const errorData = await response.json();
  throw new APIError(
    errorData?.message || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

// ==================== USER PROFILE ====================

/**
 * GET /api/user-profile
 * Fetch current user's profile with roles and permissions
 */
export async function fetchUserProfile(): Promise<UserProfile> {
  return get<UserProfile>(`${apiBaseUrl}/user-profiles`);
}

// ==================== PERMISSIONS ====================

/**
 * GET /api/permissions
 * Get all available permissions (owner only)
 */
export async function fetchAllPermissions(): Promise<GetPermissionsResponse> {
  return get<GetPermissionsResponse>(`${apiBaseUrl}/permissions`);
}

// ==================== ROLES ====================

/**
 * GET /api/roles
 * List all roles with pagination and search
 */
export async function fetchRoles(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<ListRolesResponse> {
  const url = new URL(`${apiBaseUrl}/roles`);
  if (params?.page) url.searchParams.append("page", params.page.toString());
  if (params?.pageSize)
    url.searchParams.append("pageSize", params.pageSize.toString());
  if (params?.search) url.searchParams.append("search", params.search);

  return get<ListRolesResponse>(url.toString());
}

/**
 * GET /api/roles/:id
 * Get a single role by ID
 */
export async function fetchRoleById(id: string): Promise<{ role: Role }> {
  return get<{ role: Role }>(`${apiBaseUrl}/roles/${id}`);
}

/**
 * POST /api/roles
 * Create a new role
 */
export async function createRole(
  data: CreateRoleRequest,
): Promise<{ message: string; role: Role }> {
  return post<{ message: string; role: Role }>(`${apiBaseUrl}/roles`, data);
}

/**
 * PUT /api/roles/:id
 * Update a role's metadata and permissions
 */
export async function updateRole(
  id: string,
  data: UpdateRoleRequest,
): Promise<{ message: string; role: Role }> {
  return put<{ message: string; role: Role }>(
    `${apiBaseUrl}/roles/${id}`,
    data,
  );
}

/**
 * DELETE /api/roles/:id
 * Delete a role (with optional force parameter)
 */
export async function deleteRole(
  id: string,
  force = false,
): Promise<{ message: string; usersAffected: number }> {
  const url = new URL(`${apiBaseUrl}/roles/${id}`);
  if (force) url.searchParams.append("force", "true");

  return deleteRequest<{ message: string; usersAffected: number }>(
    url.toString(),
  );
}

/**
 * GET /api/roles/:id/users
 * List users who have this role
 */
export async function fetchUsersWithRole(
  roleId: string,
  params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  },
): Promise<ListUsersResponse> {
  const url = new URL(`${apiBaseUrl}/roles/${roleId}/users`);
  if (params?.page) url.searchParams.append("page", params.page.toString());
  if (params?.pageSize)
    url.searchParams.append("pageSize", params.pageSize.toString());
  if (params?.search) url.searchParams.append("search", params.search);

  return get<ListUsersResponse>(url.toString());
}

// ==================== USER ROLES ====================

/**
 * GET /api/user-roles
 * List all users with their roles
 */
export async function fetchUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  username?: string;
  email?: string;
  userSub?: string;
  role?: string;
  permission?: string;
  segment?: string;
  cognitoPool?: string;
  emailAccountIssues?: "true" | "false";
  emailFeedbackRequests?: "true" | "false";
  emailNewsUpdates?: "true" | "false";
}): Promise<ListUsersResponse> {
  const url = new URL(`${apiBaseUrl}/user-roles`);
  if (params?.page) url.searchParams.append("page", params.page.toString());
  if (params?.pageSize)
    url.searchParams.append("pageSize", params.pageSize.toString());
  if (params?.search) url.searchParams.append("search", params.search);
  if (params?.username) url.searchParams.append("username", params.username);
  if (params?.email) url.searchParams.append("email", params.email);
  if (params?.userSub) url.searchParams.append("userSub", params.userSub);
  if (params?.role) url.searchParams.append("role", params.role);
  if (params?.permission)
    url.searchParams.append("permission", params.permission);
  if (params?.segment) url.searchParams.append("segment", params.segment);
  if (params?.cognitoPool)
    url.searchParams.append("cognitoPool", params.cognitoPool);
  if (params?.emailAccountIssues)
    url.searchParams.append("emailAccountIssues", params.emailAccountIssues);
  if (params?.emailFeedbackRequests)
    url.searchParams.append(
      "emailFeedbackRequests",
      params.emailFeedbackRequests,
    );
  if (params?.emailNewsUpdates)
    url.searchParams.append("emailNewsUpdates", params.emailNewsUpdates);

  return get<ListUsersResponse>(url.toString());
}

/**
 * POST /api/user-roles/:userSub/roles
 * Assign roles to a user
 */
export async function assignRolesToUser(
  userSub: string,
  data: AssignRolesRequest,
): Promise<{ message: string; userProfile: UserProfile }> {
  return post<{ message: string; userProfile: UserProfile }>(
    `${apiBaseUrl}/user-roles/${userSub}/roles`,
    data,
  );
}

/**
 * DELETE /api/user-roles/:userSub/roles/:roleId
 * Remove a role from a user
 */
export async function removeRoleFromUser(
  userSub: string,
  roleId: string,
): Promise<{ message: string; userProfile: UserProfile }> {
  return deleteRequest<{ message: string; userProfile: UserProfile }>(
    `${apiBaseUrl}/user-roles/${userSub}/roles/${roleId}`,
  );
}

/**
 * GET /api/user-roles/:userSub/permissions
 * Get effective permissions for a user
 */
export async function fetchUserPermissions(
  userSub: string,
): Promise<GetUserPermissionsResponse> {
  return get<GetUserPermissionsResponse>(
    `${apiBaseUrl}/user-roles/${userSub}/permissions`,
  );
}
export async function exportUsersCsv(params: {
  search?: string;
  role?: string;
  segment?: string;
  cognitoPool?: string;
  emailAccountIssues?: "true" | "false";
  emailFeedbackRequests?: "true" | "false";
  emailNewsUpdates?: "true" | "false";
  fields: string[];
}): Promise<Blob> {
  const url = new URL(`${apiBaseUrl}/user-roles/export-csv`);
  if (params.search) url.searchParams.append("search", params.search);
  if (params.role) url.searchParams.append("role", params.role);
  if (params.segment) url.searchParams.append("segment", params.segment);
  if (params.cognitoPool)
    url.searchParams.append("cognitoPool", params.cognitoPool);
  if (params.emailAccountIssues)
    url.searchParams.append("emailAccountIssues", params.emailAccountIssues);
  if (params.emailFeedbackRequests)
    url.searchParams.append(
      "emailFeedbackRequests",
      params.emailFeedbackRequests,
    );
  if (params.emailNewsUpdates)
    url.searchParams.append("emailNewsUpdates", params.emailNewsUpdates);
  if (params.fields.length > 0)
    url.searchParams.append("fields", params.fields.join(","));

  const response = await fetch(url.toString(), {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`CSV export failed: ${response.statusText}`);
  }

  return response.blob();
}

export async function fetchUserRoleAuditLogs(params?: {
  search?: string;
  limit?: number;
  skip?: number;
}): Promise<{
  logs: UserRoleAuditLog[];
  total: number;
  limit: number;
  skip: number;
}> {
  const url = new URL(`${apiBaseUrl}/user-role-audit-logs`);
  if (params?.search) url.searchParams.append("search", params.search);
  if (params?.limit) url.searchParams.append("limit", params.limit.toString());
  if (params?.skip) url.searchParams.append("skip", params.skip.toString());

  return get<{
    logs: UserRoleAuditLog[];
    total: number;
    limit: number;
    skip: number;
  }>(url.toString());
}
